import type {
  AdsSpendDeclineAccountQuery,
  AdsSpendDeclineConfigQuery,
  AdsSpendDeclineRecordQuery,
  ProcessAdsSpendDeclineRecordsParams,
  RawAdsSpendDeclineAccountRow,
  RawAdsSpendDeclineConfigRow,
  RawAdsSpendDeclineGlobalConfig,
  RawAdsSpendDeclineRecordRow,
  SaveAdsSpendDeclineGlobalConfigParams,
  UpsertAdsSpendDeclineAccountConfigParams,
} from '#/api/modules/common/types'
import type { PagingRequest } from '#/api/type'
import type { MediaPlatform } from '#/types'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'

export const adsSpendDeclineApi = defineApi(
  'operation/adsSpendDecline',
  ({ prefix }) => {
    function queryConfigPage(
      params: PagingRequest<AdsSpendDeclineConfigQuery>,
    ) {
      return requestClient
        .post(prefix('queryConfigPage'), {
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
          orderBy: params.orderBy,
          condition: {
            mediaPlatform: params.condition?.mediaPlatform,
            accountId: params.condition?.accountId,
            accountName: params.condition?.accountName,
          },
        })
        .then((res) => {
          const globalConfig: RawAdsSpendDeclineGlobalConfig = res?.globalConfig
          return {
            globalConfig: {
              minSpend: Number(globalConfig?.minJudgeSpend ?? 0),
              thresholdPercent: Number(globalConfig?.declineThresholdPct ?? 0),
              dailyExecutionTime: globalConfig.runTime || '',
              reminderEnabled: globalConfig.enabled === 1,
              updatedBy: globalConfig?.updatedBy || '',
              updatedTime: globalConfig?.updatedTime || '',
            },
            list: (res?.list || []).map(
              (item: RawAdsSpendDeclineConfigRow) => ({
                id: Number(item.id ?? 0),
                mediaPlatform: item.mediaPlatform as MediaPlatform,
                accountId: item.accountId,
                accountName: item.accountName,
                minSpend: Number(item.minJudgeSpend ?? 0),
                thresholdPercent: Number(item.declineThresholdPct ?? 0),
                updatedBy: item.updatedBy,
                updatedTime: item.updatedTime,
              }),
            ),
            total: res?.total,
          }
        })
    }

    function updateGlobalConfig(params: SaveAdsSpendDeclineGlobalConfigParams) {
      return requestClient.post(prefix('updateGlobalConfig'), {
        minJudgeSpend: Number(params.minSpend),
        declineThresholdPct: Number(params.thresholdPercent),
        runTime: params.dailyExecutionTime,
        enabled: params.reminderEnabled ? 1 : 2,
      })
    }

    function upsertAccountConfig(
      params: UpsertAdsSpendDeclineAccountConfigParams,
    ) {
      return requestClient.post(prefix('upsertAccountConfig'), {
        id: params.id,
        mediaPlatform: params.mediaPlatform,
        accountId: params.accountId,
        minJudgeSpend: Number(params.minSpend),
        declineThresholdPct: Number(params.thresholdPercent),
      })
    }

    function queryAdsAccounts(
      params: PagingRequest<AdsSpendDeclineAccountQuery>,
    ) {
      return requestClient
        .post(prefix('queryAdsAccounts'), {
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          condition: {
            mediaPlatform: params.condition?.mediaPlatform,
            account: params.condition?.account,
          },
        })
        .then((res) => ({
          list: (res?.list || []).map((item: RawAdsSpendDeclineAccountRow) => ({
            id: Number(item.id ?? 0),
            mediaPlatform: item.mediaPlatform as MediaPlatform,
            accountId: item.accountId,
            accountName: item.accountName,
            agency: item.agency,
            hasExclusiveConfig: Number(item.hasExclusiveConfig ?? 0),
          })),
          total: res?.total,
        }))
    }

    function queryRecordPage(
      params: PagingRequest<AdsSpendDeclineRecordQuery>,
    ) {
      return requestClient
        .post(prefix('queryRecordPage'), {
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          condition: {
            executeDate: params.condition?.executeDate,
            customerName: params.condition?.customerName,
            bdOpenIds: params.condition?.bdOpenIds,
            amOpenIds: params.condition?.amOpenIds,
            mediaPlatform: params.condition?.mediaPlatform,
            processStatus: params.condition?.processStatus,
            account: params.condition?.account,
          },
        })
        .then((res) => ({
          list: (res?.list || []).map((item: RawAdsSpendDeclineRecordRow) => ({
            id: Number(item.id ?? 0),
            executeDate: item.executeDate,
            customerName: item.customerName,
            bdOpenId: item.bdOpenId,
            amOpenId: item.amOpenId,
            mediaPlatform: item.mediaPlatform as MediaPlatform,
            accountId: item.accountId,
            accountName: item.accountName,
            t2Spend: Number(item.t2Spend ?? 0),
            t1Spend: Number(item.t1Spend ?? 0),
            declineRatePct: Number(item.declineRatePct ?? 0),
            appliedThresholdPct: Number(item.appliedThresholdPct ?? 0),
            processStatus: Number(item.processStatus ?? 1),
            processedTime: item.processedTime,
            processedBy: item.processedBy,
            remark: item.remark,
          })),
          total: res?.total,
        }))
    }

    function processRecords(params: ProcessAdsSpendDeclineRecordsParams) {
      return requestClient.post(prefix('processRecords'), {
        ids: params.ids,
        processStatus: params.processStatus,
        remark: params.remark,
      })
    }

    return {
      processRecords,
      queryAdsAccounts,
      queryConfigPage,
      queryRecordPage,
      updateGlobalConfig,
      upsertAccountConfig,
    }
  },
)
